import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem, Pagination, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Chip, FormControlLabel, Switch
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import locationService from '../../services/locationService';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';

const defaultForm = {
  name: '',
  address: '',
  city: '',
  phone: '',
  email: '',
  status: 'ACTIVE',
  enableWhatsappShare: false,
};

const LocationsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogMessage, setLimitDialogMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await locationService.list({ page: page - 1, size, sort: 'name', direction: 'ASC' });
      setRows(res?.content || []);
      setTotalPages(res?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Set empty state on error to prevent crashes
      setRows([]);
      setTotalPages(1);
      // Show error message to user
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load locations';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size]);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    if (!row || !row.id) {
      console.error('Invalid row data for editing:', row);
      enqueueSnackbar('Invalid location data', { variant: 'error' });
      return;
    }
    setEditing(row);
    setForm({
      name: row.name || '',
      address: row.address || '',
      city: row.city || '',
      phone: row.phone || '',
      email: row.email || '',
      status: row.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validate required fields and formats
    const errors = {};
    if (!form.name.trim()) {
      errors.name = 'Location name is required';
    }
    if (form.phone) {
      const digits = String(form.phone).replace(/\D/g, '').slice(0, 15);
      if (digits.length === 0) {
        errors.phone = 'Phone must contain digits';
      } else if (digits.length > 15) {
        errors.phone = 'Phone must be at most 15 digits';
      }
    }
    if (form.email) {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (!pattern.test(form.email)) {
        errors.email = 'Invalid email format';
      }
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const payload = { ...form };
    try {
      if (editing) {
        await locationService.update(editing.id, payload);
        enqueueSnackbar('Location updated successfully', { variant: 'success' });
      } else {
        await locationService.create(payload);
        enqueueSnackbar('Location created successfully', { variant: 'success' });
      }
      setDialogOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving location:', error);
      const msg = error.response?.data?.message || 'Failed to save location';
      if (/limit|exceed|maximum|reached/i.test(String(msg))) {
        setLimitDialogMessage(String(msg));
        setLimitDialogOpen(true);
      } else {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    }
  };

  const handleDelete = async (row) => {
    if (!row || !row.id) {
      console.error('Invalid row data for deletion:', row);
      enqueueSnackbar('Invalid location data', { variant: 'error' });
      return;
    }
    if (window.confirm(`Delete location "${row.name || 'this location'}"?`)) {
      try {
        await locationService.remove(row.id);
        enqueueSnackbar('Location deleted successfully', { variant: 'success' });
        await fetchData();
      } catch (error) {
        console.error('Error deleting location:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete location';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'phone') {
      v = value.replace(/\D/g, '').slice(0, 15);
    }
    setForm(prev => ({ ...prev, [name]: v }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'email') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (v && !pattern.test(v)) {
        setFormErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      }
    }
  };

  const content = (
    <Box p={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Locations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Location</Button>
      </Stack>

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows && rows.length > 0 && rows.map(row => (
                  <TableRow key={row?.id || Math.random()} hover>
                    <TableCell>{row?.name || '-'}</TableCell>
                    <TableCell>{row?.address || '-'}</TableCell>
                    <TableCell>{row?.city || '-'}</TableCell>
                    <TableCell>{row?.phone || '-'}</TableCell>
                    <TableCell>{row?.email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={row?.status || 'UNKNOWN'}
                        color={row?.status === 'ACTIVE' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(row)} disabled={!row?.id}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)} disabled={!row?.id}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {(!rows || rows.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {loading ? 'Loading...' : 'No locations'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Pagination
              count={totalPages || 1}
              page={page}
              onChange={(e, v) => setPage(v)}
              disabled={loading}
            />
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Location' : 'New Location'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField label="Name" name="name" value={form.name} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Address" name="address" value={form.address} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="City" name="city" value={form.city} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.email}
                helperText={formErrors.email}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label="Status" name="status" value={form.status} onChange={handleChange} fullWidth>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Limit Reached</DialogTitle>
        <DialogContent>
          <Typography>{limitDialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Layout>
      {content}
    </Layout>
  );
};

export default LocationsPage;


